import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { PerformanceMetrics } from '../types/dashboard';
import { formatPercentage, formatExecutionTime } from '../utils/formatters';

interface PerformanceMetricsCardProps {
  metrics: PerformanceMetrics;
  loading?: boolean;
}

const PerformanceMetricsCard: React.FC<PerformanceMetricsCardProps> = ({ metrics, loading = false }) => {
  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp color="success" />;
      case 'down':
        return <TrendingDown color="error" />;
      case 'stable':
        return <TrendingFlat color="action" />;
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      case 'stable':
        return 'default';
    }
  };

  return (
    <Box>
      {/* Execution Time Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" component="div">
              Execution Time
            </Typography>
            <Chip
              icon={getTrendIcon(metrics.executionTime.trend)}
              label={formatExecutionTime(metrics.executionTime.current)}
              color={getTrendColor(metrics.executionTime.trend) as any}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Average: {formatExecutionTime(metrics.executionTime.average)}
          </Typography>
        </CardContent>
      </Card>

      {/* Success Rate Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" component="div">
              Success Rate
            </Typography>
            <Chip
              icon={getTrendIcon(metrics.successRate.trend)}
              label={formatPercentage(metrics.successRate.current)}
              color={getTrendColor(metrics.successRate.trend) as any}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Average: {formatPercentage(metrics.successRate.average)}
          </Typography>
        </CardContent>
      </Card>

      {/* Total Alerts Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" component="div" mb={2}>
            Total Alerts
          </Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Today:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {metrics.totalAlerts.today}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              This Week:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {metrics.totalAlerts.thisWeek}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              This Month:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {metrics.totalAlerts.thisMonth}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerformanceMetricsCard;
